import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  ListItem,
} from '@/components/ui';

import React, { ReactNode } from 'react';

import {
  IconChevronDown,
  IconChevronUp,
  IconDots,
  IconEyeOff,
  IconSelector,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { Column } from '@tanstack/react-table';
import clsx from 'clsx';

interface DataTableColumnHeaderProps<TData, TValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  column: Column<TData, TValue>;
  title: ReactNode;
  canSort?: boolean;
  canHide?: boolean;
  infoSlot?: ReactNode; // Optional slot for additional info (e.g., hover card with field info)
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  canSort = true,
  canHide = true,
  infoSlot,
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!canSort) {
    return (
      <Flex
        align="center"
        gap="2"
        className={clsx('data-table-column-header', className)}
        {...props}
      >
        <span className="data-table-column-header__title">{title}</span>
        {infoSlot}
      </Flex>
    );
  }

  const sorted = column.getIsSorted();

  return (
    <Flex
      align="center"
      gap="2"
      className={clsx('data-table-column-header', className)}
      {...props}
    >
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="ghost"
            size="small"
            className="data-table-column-header__trigger"
            endIcon={
              sorted === 'desc' ? (
                <IconChevronDown className="data-table-column-header__sort-icon" />
              ) : sorted === 'asc' ? (
                <IconChevronUp className="data-table-column-header__sort-icon" />
              ) : (
                <IconSelector className="data-table-column-header__sort-icon" />
              )
            }
          >
            <span className="data-table-column-header__title">{title}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" variant="soft">
          <DropdownMenuItem asChild>
            <ListItem
              primary="Sort ascending"
              startIcon={<IconSortAscending />}
              onClick={() => column.toggleSorting(false)}
            />
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <ListItem
              primary="Sort descending"
              startIcon={<IconSortDescending />}
              onClick={() => column.toggleSorting(true)}
            />
          </DropdownMenuItem>
          {sorted && (
            <DropdownMenuItem asChild>
              <ListItem
                primary="Clear sort"
                startIcon={<IconDots />}
                onClick={() => column.clearSorting()}
              />
            </DropdownMenuItem>
          )}
          {canHide && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <ListItem
                  primary="Hide column"
                  startIcon={<IconEyeOff />}
                  onClick={() => column.toggleVisibility(false)}
                />
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {infoSlot}
    </Flex>
  );
}
